/**
 * Biometric authentication utility for Capacitor iOS/Android.
 * Wraps capacitor-native-biometric to provide Face ID / Touch ID / Fingerprint login.
 *
 * Stores the user's email + password (not a JWT) via NativeBiometric.setCredentials()
 * on both platforms. On biometric verification the real login API is called with the
 * stored credentials to obtain a fresh JWT, eliminating token-expiration issues.
 *
 * iOS:     Keychain (hardware-encrypted).
 * Android: EncryptedSharedPreferences via capacitor-native-biometric.
 */

import { Capacitor } from '@capacitor/core';

const BIOMETRIC_SERVER = 'protrener.app';
const BIOMETRIC_ENABLED_KEY = 'biometric_enabled';
const BIOMETRIC_PROMPTED_KEY = 'biometric_prompted';
const LEGACY_ANDROID_TOKEN_KEY = 'biometric_saved_token';

// Short-lived cache for iOS credentials read by hasSavedCredentials()
// so getBiometricCredentials() can skip the second Keychain read.
let _cachedIOSCredentials = null;
let _cachedIOSCredentialsAt = 0;
const IOS_CACHE_TTL = 30000; // 30s

/**
 * Lazy-load the NativeBiometric plugin. Returns it wrapped in an object to
 * avoid the Capacitor proxy's `.then()` trap — returning a plugin directly
 * from an async function causes JS Promise resolution to call `.then()` on it,
 * which Android's proxy rejects with "not implemented".
 */
async function loadNativeBiometric() {
  const mod = await import('capacitor-native-biometric');
  return { plugin: mod.NativeBiometric };
}

/**
 * Check if biometrics are available on this device.
 * Returns { isAvailable, biometryType } where biometryType is
 * 'faceId', 'touchId', 'fingerprint', or 'none'.
 */
export async function isBiometricAvailable() {
  if (!Capacitor.isNativePlatform()) {
    return { isAvailable: false, biometryType: 'none' };
  }

  try {
    const { plugin: NativeBiometric } = await loadNativeBiometric();
    const result = await NativeBiometric.isAvailable();

    let biometryType = 'none';
    // BiometryType enum: 1 = touchId/fingerprint, 2 = faceId, 3 = iris, 4 = multiple
    if (result.biometryType === 1) {
      biometryType = Capacitor.getPlatform() === 'android' ? 'fingerprint' : 'touchId';
    } else if (result.biometryType === 2) {
      biometryType = 'faceId';
    } else if (result.biometryType === 3) {
      biometryType = 'fingerprint';
    } else if (result.biometryType === 4) {
      biometryType = Capacitor.getPlatform() === 'android' ? 'fingerprint' : 'touchId';
    }

    return { isAvailable: result.isAvailable, biometryType };
  } catch (err) {
    console.warn('Biometric availability check failed:', err);
    return { isAvailable: false, biometryType: 'none' };
  }
}

/**
 * Store email + password for biometric login (both platforms).
 * No biometric prompt is triggered during save.
 * @returns {boolean} true if credentials were saved successfully
 */
export async function saveBiometricCredentials(email, password) {
  try {
    const { plugin: NativeBiometric } = await loadNativeBiometric();
    await NativeBiometric.setCredentials({
      server: BIOMETRIC_SERVER,
      username: email,
      password: password,
    });
    return true;
  } catch (err) {
    console.error('[Biometric] Failed to save credentials:', err?.message || err);
    return false;
  }
}

/**
 * Trigger biometric prompt and retrieve stored email + password.
 * Android and iOS have different biometric flows:
 *   - Android: check credentials exist BEFORE showing BiometricPrompt
 *     (EncryptedSharedPreferences can lose data on some OEMs)
 *   - iOS: Keychain is reliable, verify identity first then fetch
 * @returns {Promise<{ email: string, password: string }>}
 * @throws If biometric verification fails, is cancelled, or no credentials exist
 */
export async function getBiometricCredentials() {
  const { plugin: NativeBiometric } = await loadNativeBiometric();
  const platform = Capacitor.getPlatform();

  if (platform === 'android') {
    return getBiometricCredentialsAndroid(NativeBiometric);
  }
  return getBiometricCredentialsIOS(NativeBiometric);
}

async function getBiometricCredentialsAndroid(NativeBiometric) {
  // Pre-check: confirm credentials still exist before prompting user
  let preCheck;
  try {
    preCheck = await NativeBiometric.getCredentials({ server: BIOMETRIC_SERVER });
  } catch {
    throw new Error('No saved credentials on Android');
  }
  if (!preCheck?.username || !preCheck?.password) {
    throw new Error('No saved credentials on Android');
  }

  await NativeBiometric.verifyIdentity({
    reason: 'Prijavite se u ProTrener',
    title: 'Biometrijska prijava',
    subtitle: 'Potvrdite identitet',
    description: 'Upotrijebite otisak prsta za prijavu',
    useFallback: true,
    maxAttempts: 3,
  });

  const credentials = await NativeBiometric.getCredentials({ server: BIOMETRIC_SERVER });
  if (!credentials?.username || !credentials?.password) {
    throw new Error('Credentials lost after verification');
  }
  return { email: credentials.username, password: credentials.password };
}

async function getBiometricCredentialsIOS(NativeBiometric) {
  await NativeBiometric.verifyIdentity({
    reason: 'Log in to ProTrener',
    title: 'Biometric Login',
    subtitle: 'Verify your identity',
    description: 'Use Face ID or Touch ID to sign in',
  });

  // Use cached credentials from hasSavedCredentials() if still fresh
  if (_cachedIOSCredentials && (Date.now() - _cachedIOSCredentialsAt) < IOS_CACHE_TTL) {
    const cached = _cachedIOSCredentials;
    _cachedIOSCredentials = null;
    return { email: cached.username, password: cached.password };
  }

  const credentials = await NativeBiometric.getCredentials({ server: BIOMETRIC_SERVER });
  if (!credentials?.username || !credentials?.password) {
    throw new Error('No saved credentials found');
  }
  return { email: credentials.username, password: credentials.password };
}

/**
 * Remove stored biometric credentials from both platforms.
 * Also cleans up the legacy Android localStorage token.
 */
export async function deleteBiometricCredentials() {
  if (!Capacitor.isNativePlatform()) return;

  // Clean up legacy Android localStorage token
  localStorage.removeItem(LEGACY_ANDROID_TOKEN_KEY);

  try {
    const { plugin: NativeBiometric } = await loadNativeBiometric();
    await NativeBiometric.deleteCredentials({ server: BIOMETRIC_SERVER });
  } catch (err) {
    console.warn('Delete biometric credentials:', err);
  }
}

/**
 * Check if the user has enabled biometric login.
 */
export function isBiometricEnabled() {
  return localStorage.getItem(BIOMETRIC_ENABLED_KEY) === 'true';
}

/**
 * Set the biometric login enabled/disabled flag.
 */
export function setBiometricEnabled(enabled) {
  if (enabled) {
    localStorage.setItem(BIOMETRIC_ENABLED_KEY, 'true');
  } else {
    localStorage.removeItem(BIOMETRIC_ENABLED_KEY);
  }
}

/**
 * Check if the user has already been prompted to enable biometrics.
 */
export function wasBiometricPrompted() {
  return localStorage.getItem(BIOMETRIC_PROMPTED_KEY) === 'true';
}

/**
 * Mark the user as having been prompted for biometric login.
 */
export function setBiometricPrompted() {
  localStorage.setItem(BIOMETRIC_PROMPTED_KEY, 'true');
}

/**
 * Clear all biometric-related state (full reset).
 */
export async function clearBiometricData() {
  await deleteBiometricCredentials();
  localStorage.removeItem(BIOMETRIC_ENABLED_KEY);
  localStorage.removeItem(BIOMETRIC_PROMPTED_KEY);
}

/**
 * Get a human-readable label for the biometry type.
 */
export function getBiometricLabel(biometryType) {
  switch (biometryType) {
    case 'faceId': return 'Face ID';
    case 'touchId': return 'Touch ID';
    case 'fingerprint': return 'Fingerprint';
    case 'iris': return 'Iris';
    default: return 'Biometric';
  }
}

/**
 * Check if biometric credentials likely exist (without triggering a prompt).
 * 
 * IMPORTANT: On Android, NativeBiometric.getCredentials() triggers a biometric
 * prompt, so we CANNOT use it to silently check. Instead, we trust the localStorage
 * flag -- if user enabled biometrics and credentials were saved, they should exist.
 * 
 * On iOS, getCredentials() reads from Keychain without prompting, so we can verify.
 */
export async function hasSavedCredentials() {
  if (!Capacitor.isNativePlatform()) return false;
  if (!isBiometricEnabled()) return false;

  const platform = Capacitor.getPlatform();

  if (platform === 'android') {
    // On Android, we cannot check without prompting. Trust the enabled flag.
    // If credentials were lost (rare), the actual login attempt will fail
    // and we'll clear the flag then.
    return true;
  }

  // iOS: Keychain read doesn't prompt, so we can verify.
  // Cache the result so getBiometricCredentials() can skip the second read.
  try {
    const { plugin: NativeBiometric } = await loadNativeBiometric();
    const credentials = await NativeBiometric.getCredentials({
      server: BIOMETRIC_SERVER,
    });
    if (credentials?.username && credentials?.password) {
      _cachedIOSCredentials = credentials;
      _cachedIOSCredentialsAt = Date.now();
      return true;
    }
    return false;
  } catch (err) {
    console.warn('[Biometric] iOS hasSavedCredentials check failed:', err?.message || err);
    return false;
  }
}
